import styled, { css } from 'styled-components'
import type { ButtonBaseProps } from './Button.types'

export const StyledButton = styled.button<
  ButtonBaseProps & { $loading?: boolean }
>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  padding: 1.1rem;
  text-align: center;
  border: 1px solid;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  cursor: ${({ $loading }) => {
    return $loading ? 'wait' : 'pointer'
  }};

  ${({ theme, variant, $loading }) => {
    switch (variant) {
      case undefined:
      case 'primary': {
        return css`
          color: ${theme.colors.text.secondary};
          fill: ${theme.colors.text.secondary};
          background: ${theme.colors.primary};
          border-color: ${theme.colors.primary};
          border-radius: 0;

          g,
          path {
            transition: all 0.05s ease-in-out;

            fill: ${theme.colors.text.secondary};
            color: ${theme.colors.text.secondary};
          }

          &:focus {
            color: ${theme.colors.text.secondary};
          }

          @media (hover: hover) {
            &:hover {
              color: ${theme.colors.text.secondary};
              background: ${theme.colors.primaryLight};
              border-color: ${theme.colors.primaryLight};
            }
          }

          &:disabled {
            cursor: ${$loading ? 'wait' : 'not-allowed'};
            background: ${$loading
              ? theme.colors.primary
              : theme.colors.disabled};
            border-color: ${$loading
              ? theme.colors.primary
              : theme.colors.disabled};
          }
        `
      }
      case 'ghost': {
        return css`
          background: transparent;
          color: ${theme.colors.primary};
          border-color: ${theme.colors.primary};

          g,
          path {
            transition: all 0.05s ease-in-out;

            fill: ${theme.colors.primary};
            color: ${theme.colors.primary};
          }

          &:focus {
            color: ${theme.colors.primary};
          }

          @media (hover: hover) {
            &:hover {
              background: ${theme.colors.primary};
              color: ${theme.colors.text.secondary};
              border-color: ${theme.colors.primary};

              g,
              path {
                fill: ${theme.colors.text.secondary};
                color: ${theme.colors.text.secondary};
              }
            }
          }

          &:disabled {
            background: transparent;
            color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};

            g,
            path {
              fill: ${theme.colors.primary};
              color: ${theme.colors.primary};
            }
          }
        `
      }
      case 'outline-dark': {
        return css`
          background: transparent;
          color: ${theme.colors.text.primary};
          border-color: ${theme.colors.text.primary};

          g,
          path {
            transition: all 0.05s ease-in-out;

            fill: ${theme.colors.text.primary};
            color: ${theme.colors.text.primary};
          }

          &:focus {
            color: ${theme.colors.text.primary};
          }

          @media (hover: hover) {
            &:hover {
              background: ${theme.colors.text.primary};
              color: ${theme.colors.text.secondary};
              border-color: ${theme.colors.text.primary};

              g,
              path {
                fill: ${theme.colors.text.secondary};
                color: ${theme.colors.text.secondary};
              }
            }
          }

          &:disabled {
            background: transparent;
            color: ${theme.colors.text.primary};
            border-color: ${theme.colors.text.primary};

            g,
            path {
              fill: ${theme.colors.text.primary};
              color: ${theme.colors.text.primary};
            }
          }
        `
      }
      case 'frosted': {
        return css`
          border: 1px solid transparent;
          background: ${theme.colors.text.primary}50;
          color: ${theme.colors.text.secondary};
          fill: ${theme.colors.text.secondary};
          padding: 1rem;
          font-weight: 600;

          g,
          path {
            transition: all 0.05s ease-in-out;

            fill: ${theme.colors.text.secondary};
            color: ${theme.colors.text.secondary};
          }

          &:focus {
            color: ${theme.colors.text.secondary};
          }

          @media (hover: hover) {
            &:hover {
              background: ${theme.colors.text.primary}99;
              border: 1px solid ${theme.colors.text.secondary};
            }
          }

          &:disabled {
            border: none;
            background: ${theme.colors.text.primary}1A;
            color: ${theme.colors.text.secondary};
            fill: ${theme.colors.text.secondary};

            g,
            path {
              fill: ${theme.colors.text.secondary};
              color: ${theme.colors.text.secondary};
            }
          }
        `
      }
      case 'ghostWhite': {
        return css`
          background: transparent;
          color: ${theme.colors.text.secondary};
          border-color: ${theme.colors.text.secondary};

          g,
          path {
            transition: all 0.05s ease-in-out;

            fill: ${theme.colors.text.secondary};
            color: ${theme.colors.text.secondary};
          }

          &:focus {
            color: ${theme.colors.text.secondary};
          }

          @media (hover: hover) {
            &:hover {
              background: ${theme.colors.text.secondary};
              color: ${theme.colors.text.primary};

              g,
              path {
                fill: ${theme.colors.text.primary};
                color: ${theme.colors.text.primary};
              }
            }
          }

          &:disabled {
            background: transparent;
            color: ${theme.colors.text.secondary};
            border-color: ${theme.colors.text.secondary};

            g,
            path {
              fill: ${theme.colors.text.secondary};
              color: ${theme.colors.text.secondary};
            }
          }
        `
      }
    }
  }}

  /* TODO: SHOP-2094 */
  /* Reset anchor <a> styles coming from index.css */
  font-size: unset;
  line-height: unset;

  &:hover {
    text-decoration: none;

    /* TODO: SHOP-5175 */
    /* Reset anchor <a> styles coming from bootstrap.min.css */
    @media (max-width: 991px) {
      color: ${({ theme }) => {
        return theme.colors.text.secondary
      }};
    }
  }

  ${({ fullWidth }) => {
    return (
      fullWidth &&
      css`
        width: 100%;
      `
    )
  }}

  span {
    line-height: 1;
  }
`
